import React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import {
  LuUser,
  LuFileBadge2,
  LuFileBadge,
  LuStethoscope,
  LuClipboardList,
  LuBaby,
  LuShield,
  LuContact,
  LuBuilding2,
  LuLayoutGrid,
  LuPackage,
  LuCalendarPlus,
  LuHash,
  LuCalendar,
  LuGitBranch,
  LuSkipBack,
  LuTable2,
  LuCalendarDays,
  LuArrowLeftRight,
  LuLink2,
  LuTag,
  LuGlobe,
  LuCreditCard,
  LuCopy,
  LuRotateCcw,
  LuArrowRight,
  LuCheckCircle,
  LuXCircle,
  LuClock,
  LuListChecks,
  LuShieldCheck,
  LuShieldAlert,
  LuGauge,
} from "react-icons/lu";
import ReferralFlag from "../../../../components/ReferralFlag";

const formatDateTime = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return `${day}-${month}-${year} ${hours}:${minutes}`;
};


function Pill({ children, tone = "gray" }) {
  const tones = {
    blue: "bg-blue-50 text-blue-600",
    blueSolid: "bg-blue-100 text-blue-700",
    green: "bg-green-50 text-green-700",
    gray: "bg-gray-100 text-gray-400",
  };
  return (
    <span
      className={`inline-flex items-center justify-center rounded-full px-3 py-0.5 text-[13px] font-semibold ${tones[tone]}`}
    >
      {children}
    </span>
  );
}

// Clickable value with copy-to-clipboard feedback
function CopyValue({ value }) {
  const [copied, setCopied] = useState(false);

  if (value == null || value === "") return "—";

  const handleCopy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(String(value));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard unavailable — ignore
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      title="Copy"
      className="inline-flex max-w-full items-center gap-1.5 text-[#2b6da8] hover:text-[#0070C2] transition-colors"
    >
      <span className="break-all text-left">{value}</span>
      {copied ? (
        <CheckOutlinedIcon sx={{ fontSize: 14 }} className="text-green-600" />
      ) : (
        <ContentCopyOutlinedIcon
          sx={{ fontSize: 14 }}
          className="shrink-0 text-slate-400"
        />
      )}
    </button>
  );
}

function Row({ icon: Icon, label, value }) {
  const isEmpty = value === undefined || value === null || value === "";
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-dotted border-slate-200 last:border-b-0">
      <div className="flex items-center gap-2 text-[13px] font-semibold text-slate-500">
        <Icon size={16} className="text-slate-400 shrink-0" />
        <span>{label}</span>
      </div>
      <div className="text-[15px] font-bold text-slate-900 text-right">
        {isEmpty ? <span className="text-gray-300 font-normal">—</span> : value}
      </div>
    </div>
  );
}

function SectionHeader({ icon: Icon, title, circled = false }) {
  return (
    <div className="mb-1.5">
      <div className="flex items-center gap-2 pb-1.5">
        {circled ? (
          <span className="flex items-center justify-center w-5 h-5 rounded-full bg-[#0070C2]">
            <Icon size={12} className="text-white" fill="white" />
          </span>
        ) : (
          <Icon
            size={17}
            className="text-[#0070C2]"
            fill={title?.toUpperCase().startsWith("INSURANCE") ? "#0070C2" : "none"}
          />
        )}
        <h3 className="text-[15px] font-bold tracking-wide text-slate-700">
          {title}
        </h3>
      </div>
      <div className="border-b border-slate-200 -mx-0" />
    </div>
  );
}


export default function TransactionOverviewCards({ item, mode = "preauth" }) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";

  const type = item?.type;
  const subtype = item?.subtype;
  const status = item?.status || item?.adjudication_outcome;

  const statusTone =
    status === "approved" || status === "active"
      ? "text-green-600"
      : status === "rejected"
        ? "text-red-600"
        : status === "pending"
          ? "text-amber-500"
          : status === "queued"
            ? "text-blue-600"
            : "text-slate-900";

  const statusDot =
    status === "pending"
      ? "bg-amber-400"
      : status === "queued"
        ? "bg-blue-500"
        : status === "approved" || status === "active"
          ? "bg-green-500"
          : status === "rejected"
            ? "bg-red-500"
            : "bg-gray-400";

  const outcomeTone =
    item?.outcome === "approved" || item?.outcome === "complete"
      ? "text-green-600"
      : item?.outcome === "rejected"
        ? "text-red-600"
        : item?.outcome === "queued"
          ? "text-blue-600"
          : item?.outcome === "pending"
            ? "text-amber-500"
            : "text-slate-900";

  return (
    <div
      dir={isRTL ? "rtl" : "ltr"}
      className="w-full bg-white border border-slate-200 !rounded-lg"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 px-3 sm:px-4 py-3">
        <div>
          <SectionHeader icon={LuUser} title={t("Patient Information")} circled />
          <div className="mb-3">
            <Row
              icon={LuFileBadge2}
              label={t("Beneficiary")}
              value={<CopyValue value={item?.patient_name} />}
            />
            <Row
              icon={LuFileBadge}
              label={t("Document No.")}
              value={<CopyValue value={item?.patient_document_id} />}
            />
            <Row
              icon={LuStethoscope}
              label={t("Encounter Type")}
              value={item?.subtype || item?.type}
            />
            <Row
              icon={LuClipboardList}
              label={t("Request / Sub")}
              value={
                (type || subtype) && (
                  <span className="inline-flex items-center gap-2">
                    {type && <span>{type}</span>}
                    {type && subtype && <span className="text-slate-400">/</span>}
                    {subtype && <Pill tone="blue">{subtype}</Pill>}
                  </span>
                )
              }
            />
            <Row
              icon={LuBaby}
              label={t("Is New Born")}
              value={
                item?.is_new_born == null ? null : (
                  <Pill tone={item?.is_new_born ? "blue" : "green"}>
                    {t(item?.is_new_born ? "Yes" : "No")}
                  </Pill>
                )
              }
            />
          </div>

          <SectionHeader icon={LuShield} title={t("Insurance & Request")} />
          <div>
            <Row icon={LuContact} label={t("TPA Name")} value={item?.tpa_name} />
            <Row
              icon={LuBuilding2}
              label={t("Insurer")}
              value={item?.payer_name || item?.tpa_name}
            />
            <Row
              icon={LuLayoutGrid}
              label={t("Approval Number")}
              value={<CopyValue value={item?.pre_auth_ref_no} />}
            />
            <Row
              icon={LuLayoutGrid}
              label={t(mode === "claim" ? "Referral Number" : "Offline Pre Auth Ref. No.")}
              value={<CopyValue value={item?.offline_pre_auth_no} />}
            />
            <Row
              icon={LuCalendar}
              label={t(mode === "claim" ? "Referral Date" : "Offline Pre Auth Ref Date")}
              value={formatDateTime(item?.offline_pre_auth_date)}
            />
            <Row
              icon={LuPackage}
              label={t("Bundle ID")}
              value={<CopyValue value={item?.request_bundle_id} />}
            />
            <Row
              icon={LuCalendarPlus}
              label={t("Date Ordered")}
              value={formatDateTime(item?.date_ordered)}
            />
            <Row
              icon={LuHash}
              label={t("PreAuth Res. ID")}
              value={<CopyValue value={item?.pre_auth_ref_value} />}
            />
            <Row
              icon={LuCalendar}
              label={t("PreAuth Period")}
              value={item?.pre_auth_period}
            />
          </div>
        </div>

        <div>
          <SectionHeader icon={LuGitBranch} title={t("Tracking & References")} />
          <div className="mb-3">
            <Row
              icon={LuSkipBack}
              label={t("Follow Up")}
              value={
                item?.is_followup_req == null ? null : (
                  <Pill tone="blueSolid">
                    {t(item?.is_followup_req ? "Yes" : "No")}
                  </Pill>
                )
              }
            />
            <Row
              icon={LuTable2}
              label={t("Offline Eligibility")}
              value={<CopyValue value={item?.offline_verification_id} />}
            />
            <Row
              icon={LuCalendarDays}
              label={t("Offline Eligibility Date")}
              value={formatDateTime(item?.offline_verification_date)}
            />
            <Row
              icon={LuArrowLeftRight}
              label={t("Ref Transaction ID")}
              value={<CopyValue value={item?.followup_ref_transaction_id} />}
            />
            <Row
              icon={LuLink2}
              label={t("Related PreAuth Ref.")}
              value={<CopyValue value={item?.related_pre_auth_ref_no} />}
            />
            <Row
              icon={LuTag}
              label={t("Related Request ID")}
              value={<CopyValue value={item?.related_pre_auth_req_identifier} />}
            />
            <Row
              icon={LuGlobe}
              label={t("PreAuth Res. URL")}
              value={
                item?.pre_auth_ref_url && (
                  <a
                    href={item.pre_auth_ref_url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex max-w-full items-center gap-1.5 text-[#2b6da8] hover:text-[#0070C2] font-semibold hover:underline transition-colors"
                  >
                    <span className="break-all">{item.pre_auth_ref_url}</span>
                  </a>
                )
              }
            />
            <Row
              icon={LuCreditCard}
              label={t("Eligibility Resp. ID")}
              value={<CopyValue value={item?.verification_resp_ident_value} />}
            />
            <Row
              icon={LuCopy}
              label={t("Is Duplicate")}
              value={
                item?.is_duplicate == null ? (
                  <Pill tone="gray">—</Pill>
                ) : (
                  <Pill tone={item?.is_duplicate ? "blue" : "green"}>
                    {t(item?.is_duplicate ? "Yes" : "No")}
                  </Pill>
                )
              }
            />
            <Row
              icon={LuRotateCcw}
              label={t("Duplicate Ref")}
              value={<CopyValue value={item?.duplicate_ref} />}
            />
          </div>

          <SectionHeader icon={LuGauge} title={t("Decision & Outcome")} />
          <div className="rounded-lg bg-gray-50 border border-slate-200 px-3 py-2">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-1.5">
              <div className="flex items-center gap-2 text-[13px]">
                {status === "approved" || status === "active" ? (
                  <LuCheckCircle size={15} className="text-green-600" />
                ) : status === "rejected" ? (
                  <LuXCircle size={15} className="text-red-600" />
                ) : status === "queued" ? (
                  <LuClock size={15} className="text-blue-600" />
                ) : (
                  <span className={`w-2 h-2 rounded-full shrink-0 ${statusDot}`} />
                )}
                <span className="text-slate-500 font-medium">{t("Status")}</span>
                <span className={`font-bold uppercase ${statusTone}`}>
                  {t(status)}
                </span>
              </div>
              <div className="flex items-center gap-2 text-[13px]">
                {item?.outcome === "approved" || item?.outcome === "complete" ? (
                  <LuShieldCheck size={15} className="text-green-600" />
                ) : item?.outcome === "rejected" ? (
                  <LuShieldAlert size={15} className="text-red-600" />
                ) : item?.outcome === "queued" ? (
                  <LuListChecks size={15} className="text-blue-600" />
                ) : (
                  <LuListChecks size={15} className="text-slate-400" />
                )}
                <span className="text-slate-500 font-medium">{t("Outcome")}</span>
                <span className={`font-bold uppercase ${outcomeTone}`}>
                  {t(item?.outcome)}
                </span>
              </div>
              <div className="flex items-center gap-2 text-[13px]">
                <LuArrowRight
                  size={15}
                  className={`text-[#0070C2] ${isRTL ? "rotate-180" : ""}`}
                />
                <span className="text-slate-500 font-medium">{t("Referral")}</span>
                <ReferralFlag item={item} />
              </div>
            </div>

            {item?.disposition && (
              <div className="mt-1.5 border-t border-dotted border-slate-200 pt-1.5 flex items-start gap-1.5 text-[12px]">
                <LuShieldAlert size={13} className="text-amber-500 shrink-0 mt-0.5" />
                <span>
                  <span className="font-semibold text-slate-500">
                    {t("Disposition")}:
                  </span>{" "}
                  <span className="text-slate-700 font-medium">
                    {item.disposition}
                  </span>
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
